import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  LogIn,
  LogOut,
  Check,
  X,
  RotateCcw,
  AlertTriangle,
  ClipboardList,
  Plus,
  Loader2,
  CalendarCheck,
  CalendarPlus,
  ChevronDown,
  Calendar,
  Expand,
  Wallet,
  CheckCircle2,
  TrendingUp,
  ChevronRight,
  FileText,
  Cable,
  Wrench,
  Hammer,
  Package,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useClientContext } from '../ClientContext';
import { useClientTasks, useClientWeeklyVisits } from '@/features/visits/hooks/useClientDetails';
import { useCreateTask, useCompleteTask, useUpdateTask, useCancelTask } from '@/features/tasks/hooks/useTasks';
import { useMarkVisited } from '@/features/visits/hooks/useWeeklyVisits';
import { VisitTimeEditor, type VisitTimeEditorDraft } from '@/features/visits/VisitTimeEditor';
import { Label } from '@/components/ui/label';
import { TaskDetailSheet } from '@/features/tasks/TaskDetailSheet';
import { CreateTaskSheet } from '@/features/tasks/CreateTaskSheet';
import { WeeklyVisitsSheet } from '@/features/visits/WeeklyVisitsSheet';
import { ScheduleVisitDialog } from '@/features/visits/ScheduleVisitDialog';
import { CableUsageDialog } from '../components/CableUsageDialog';
import { ClientRemontSheet } from '../components/ClientRemontSheet';
import { WarehouseItemSheet } from '@/features/legacy/components/WarehouseItemSheet';
import { getTaskStyle } from '@/lib/priority';
import { cn } from '@/lib/utils';
import { getLegalEntityLabel } from '@shared/types';
import type { Task, UpdateTaskDto, CreateTaskDto } from '@/api/tasks';
import { warehouseApi, type WarehouseItem } from '@/api/warehouse';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'BYN',
    maximumFractionDigits: 0,
  }).format(amount);
}

function ClientWarehouseSummary({
  items,
  isLoading,
  isError,
  onItemClick,
}: {
  items: WarehouseItem[];
  isLoading: boolean;
  isError: boolean;
  onItemClick: (item: WarehouseItem) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-background">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Package className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Склад</span>
      </div>
      <div className="px-3 py-2">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Загрузка склада...</div>
        ) : isError ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Склад клиента не загрузился</span>
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            У клиента ничего нашего не стоит
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onItemClick(item)}
                className="block w-full min-w-0 rounded-md px-1 py-1 text-left transition-colors hover:bg-accent active:bg-accent"
              >
                <div className="truncate text-sm font-medium">{item.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="font-mono">{item.barcode}</span>
                  {item.quantity && <span>{item.quantity}</span>}
                  {item.assignedToName && <span>{item.assignedToName}</span>}
                </div>
                {item.comment && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                    {item.comment}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MainTab() {
  const {
    clientId,        // Real client ID for API calls
    visitTargetId,   // Visit target ID (may be virtual office ID)
    detail,
    detailLoading,
    activeVisit,
    onCheckIn,
    onCheckOut,
    onMarkSkipped,
    onUndoCheckIn,
    onSetUrgent,
    onRemoveUrgent,
    onPlanToday,
    onCancelPlanToday,
    onScheduleVisit,
    isActionLoading,
  } = useClientContext();

  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [weeklyVisitsOpen, setWeeklyVisitsOpen] = useState(false);
  const [checkoutTimeDraft, setCheckoutTimeDraft] = useState<VisitTimeEditorDraft | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [taskToConfirm, setTaskToConfirm] = useState<{ task: Task; action: 'complete' | 'cancel' } | null>(null);
  const [wasBeforeOpen, setWasBeforeOpen] = useState(false);
  const [wasBeforeDate, setWasBeforeDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [wasBeforeTime, setWasBeforeTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  });
  const [cableUsageOpen, setCableUsageOpen] = useState(false);
  const [remontOpen, setRemontOpen] = useState(false);
  const [selectedWarehouseItem, setSelectedWarehouseItem] = useState<WarehouseItem | null>(null);
  const openSubcontractorWorks = () => {
    const params = new URLSearchParams();
    params.set('clientId', String(clientId));
    if (detail?.client.name) params.set('client', detail.client.name);
    navigate(`/more/subcontractor-works?${params.toString()}`);
  };
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const { data: weeklyVisits } = useClientWeeklyVisits(clientId);
  const { data: tasks, isLoading: tasksLoading } = useClientTasks(clientId);
  const {
    data: clientWarehouseData,
    isLoading: warehouseLoading,
    error: warehouseError,
  } = useQuery({
    queryKey: ['warehouse', 'client', clientId],
    queryFn: () => warehouseApi.getByClient(clientId),
    enabled: Boolean(clientId),
    staleTime: 30_000,
  });
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const cancelTask = useCancelTask();
  const markVisited = useMarkVisited();

  // Derive state from detail
  const currentVisit = detail?.currentVisit;

  const financeSummaries = detail?.financeSummaries || [];
  const documentsSummary = detail?.documentsSummary;

  // Check if this is the active visit
  const isActive = activeVisit?.visitTargetId === visitTargetId;
  const isCompleted = currentVisit?.status === 'VISITED' || currentVisit?.status === 'SKIPPED';
  const hasOtherActiveVisit = activeVisit && !isActive;

  // Визит "активен" если plannedDate отсутствует или <= сегодня
  const todayStr = new Date().toISOString().split('T')[0];
  const isVisitActive = !currentVisit?.plannedDate || currentVisit.plannedDate <= todayStr;

  // Срочность и статусы применяются только к активным визитам
  const isUrgent = currentVisit?.priority === 'URGENT' && isVisitActive;
  const hasPlannedToday = currentVisit?.plannedDate
    ? new Date(currentVisit.plannedDate).toDateString() === new Date().toDateString()
    : false;

  // hasPendingVisit = есть PENDING визит И он уже активен (plannedDate <= сегодня)
  const hasPendingVisit = currentVisit?.status === 'PENDING' && isVisitActive;

  const pendingTasks = tasks?.filter((t) => t.status === 'PENDING') || [];
  const clientWarehouseItems = clientWarehouseData?.items ?? [];
  const isCheckoutTimeInvalid = checkoutTimeDraft?.expanded && !checkoutTimeDraft.isValid;

  const handleCheckOut = () => {
    if (isCheckoutTimeInvalid) return;
    const actionNote = note.trim() || undefined;
    const payload = {
      ...(actionNote ? { note: actionNote } : {}),
      ...(checkoutTimeDraft?.expanded
        ? {
            startedTime: checkoutTimeDraft.startedTime,
            endedTime: checkoutTimeDraft.endedTime,
            timezoneOffsetMinutes: checkoutTimeDraft.timezoneOffsetMinutes,
          }
        : {}),
    };

    setNote('');
    onCheckOut(Object.keys(payload).length > 0 ? payload : undefined);
  };

  const handleCreateTask = () => {
    if (!newTaskTitle.trim()) return;
    createTask.mutate(
      {
        clientId,
        type: 'INDOOR',
        title: newTaskTitle.trim(),
      },
      {
        onSuccess: () => {
          setNewTaskTitle('');
          setShowTaskInput(false);
        },
      }
    );
  };

  const handleCompleteTask = (taskId: string) => {
    completeTask.mutate(taskId);
    setTaskToConfirm(null);
  };

  const handleUpdateTask = (id: string, updates: UpdateTaskDto) => {
    updateTask.mutate({ id, updates });
  };

  const handleCancelTask = (id: string) => {
    cancelTask.mutate(id);
    setTaskToConfirm(null);
  };

  const handleConfirmTaskAction = () => {
    if (!taskToConfirm) return;
    if (taskToConfirm.action === 'complete') {
      handleCompleteTask(taskToConfirm.task.id);
    } else {
      handleCancelTask(taskToConfirm.task.id);
    }
  };

  const handleWasBeforeConfirm = () => {
    const visitedAt = `${wasBeforeDate}T${wasBeforeTime}:00`;
    markVisited.mutate(
      { clientId: visitTargetId, visitedAt },  // Use visitTargetId for visit operation
      {
        onSuccess: () => {
          setWasBeforeOpen(false);
        },
      }
    );
  };

  const handleCreateTaskExpanded = (dto: CreateTaskDto) => {
    createTask.mutate(dto, {
      onSuccess: () => setCreateTaskOpen(false),
    });
  };

  if (detailLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="p-4 space-y-4">
        {/* Finance Summaries - show for each legal entity */}
        {financeSummaries.map((fs) => (
          <div
            key={fs.legalEntity}
            className={cn(
              'flex items-center gap-2 p-3 rounded-lg cursor-pointer active:opacity-80 transition-opacity',
              fs.status === 'ok' && 'bg-green-50 border border-green-200 text-green-900',
              fs.status === 'debt' && 'bg-amber-50 border border-amber-200 text-amber-900',
              fs.status === 'overpayment' && 'bg-blue-50 border border-blue-200 text-blue-900'
            )}
            onClick={() => navigate(`/visits/client/${clientId}/balance/${fs.legalEntity}`)}
          >
            {fs.status === 'ok' && <CheckCircle2 className="h-4 w-4 flex-shrink-0" />}
            {fs.status === 'debt' && <Wallet className="h-4 w-4 flex-shrink-0" />}
            {fs.status === 'overpayment' && <TrendingUp className="h-4 w-4 flex-shrink-0" />}
            <span className="text-sm flex-1">
              {financeSummaries.length > 1 && (
                <span className="text-muted-foreground">{getLegalEntityLabel(fs.legalEntity, 'short')}: </span>
              )}
              {fs.status === 'ok' && 'Баланс OK'}
              {fs.status === 'debt' && (
                <>
                  Долг: <strong>{formatCurrency(Math.abs(fs.balance))}</strong>
                </>
              )}
              {fs.status === 'overpayment' && (
                <>
                  Переплата: <strong>{formatCurrency(fs.balance)}</strong>
                </>
              )}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
          </div>
        ))}

        {/* Document Status Card - unsurrendered documents */}
        {documentsSummary && (
          <div
            className={cn(
              'flex items-center gap-2 p-3 rounded-lg cursor-pointer active:opacity-80 transition-opacity',
              documentsSummary.critical > 0 && 'bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-100',
              documentsSummary.danger > 0 && !documentsSummary.critical && 'bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100',
              !documentsSummary.danger && !documentsSummary.critical && 'bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 text-green-900 dark:text-green-100'
            )}
            onClick={() => navigate(`/visits/client/${clientId}/documents`)}
          >
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm flex-1">
              {documentsSummary.total > 0 ? (
                <>
                  Документы: <strong>{documentsSummary.total}</strong>
                  {documentsSummary.danger > 0 && (
                    <span className="text-amber-600 dark:text-amber-400 ml-2">⚠ {documentsSummary.danger}</span>
                  )}
                  {documentsSummary.critical > 0 && (
                    <span className="text-red-600 dark:text-red-400 ml-2">🔴 {documentsSummary.critical}</span>
                  )}
                </>
              ) : (
                <>Документы: всё сдано ✓</>
              )}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
          </div>
        )}

        {/* Note input - only for check-out */}
        {isActive && (
          <div>
            <Input
              placeholder="Заметка о визите..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isActionLoading}
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2">
          {/* Active visit - show checkout and cancel */}
          {isActive && (
            <>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  variant="default"
                  onClick={handleCheckOut}
                  disabled={isActionLoading || isCheckoutTimeInvalid}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Завершить визит
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" disabled={isActionLoading}>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setScheduleDialogOpen(true)}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Запланировать дату
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <VisitTimeEditor
                startedAt={activeVisit?.startedAt}
                label="Будет сохранено"
                onDraftChange={setCheckoutTimeDraft}
                disabled={isActionLoading}
              />
              <Button
                className="w-full"
                variant="ghost"
                onClick={() => onUndoCheckIn(visitTargetId)}
                disabled={isActionLoading}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Не заходил
              </Button>
            </>
          )}

          {/* Not completed and no active visit elsewhere */}
          {!isActive && !isCompleted && !hasOtherActiveVisit && (
            <div className="flex gap-2">
              <Button
                className="flex-1"
                variant="default"
                onClick={() => onCheckIn(visitTargetId)}
                disabled={isActionLoading}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Начать визит
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" disabled={isActionLoading}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => setWasBeforeOpen(true)}>
                    <Check className="h-4 w-4 mr-2" />
                    Был раньше
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMarkSkipped(visitTargetId)}>
                    <X className="h-4 w-4 mr-2" />
                    Пропуск визита
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Has other active visit */}
          {hasOtherActiveVisit && !isCompleted && (
            <div className="text-center text-sm text-muted-foreground py-2">
              Сначала завершите текущий визит
            </div>
          )}

          {/* Priority and Planning */}
          {!isCompleted && !isActive && (
            <>
              {/* State 1: Already URGENT */}
              {isUrgent && (
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-amber-200 hover:bg-amber-100 text-amber-900 border-amber-400"
                    variant="outline"
                    onClick={() => onRemoveUrgent(visitTargetId)}
                    disabled={isActionLoading}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Снять срочность
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" disabled={isActionLoading}>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        className="border border-blue-300 rounded-md text-blue-800 dark:text-blue-300 focus:bg-blue-50 dark:focus:bg-blue-950"
                        onClick={() => setScheduleDialogOpen(true)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Запланировать дату
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              {/* State 2: Has planned for today */}
              {!isUrgent && hasPlannedToday && (
                <>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300"
                      variant="outline"
                      onClick={() => onSetUrgent(visitTargetId)}
                      disabled={isActionLoading}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Срочный
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" disabled={isActionLoading}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          className="border border-blue-300 rounded-md text-blue-800 dark:text-blue-300 focus:bg-blue-50 dark:focus:bg-blue-950"
                          onClick={() => setScheduleDialogOpen(true)}
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Запланировать дату
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <Button
                    className="w-full"
                    variant="ghost"
                    onClick={() => onCancelPlanToday(visitTargetId)}
                    disabled={isActionLoading}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Отменить заехать сегодня
                  </Button>
                </>
              )}

              {/* State 3: Has pending visit (not today) */}
              {!isUrgent && !hasPlannedToday && hasPendingVisit && (
                <>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300"
                      variant="outline"
                      onClick={() => onSetUrgent(visitTargetId)}
                      disabled={isActionLoading}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Срочный
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" disabled={isActionLoading}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          className="border border-orange-300 rounded-md mb-1 text-orange-800 dark:text-orange-300 focus:bg-orange-50 dark:focus:bg-orange-950"
                          onClick={() => onPlanToday(visitTargetId)}
                        >
                          <CalendarCheck className="h-4 w-4 mr-2" />
                          Заехать сегодня
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="border border-blue-300 rounded-md text-blue-800 dark:text-blue-300 focus:bg-blue-50 dark:focus:bg-blue-950"
                          onClick={() => setScheduleDialogOpen(true)}
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Запланировать дату
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <Button
                    className="w-full"
                    variant="ghost"
                    onClick={() => onCancelPlanToday(visitTargetId)}
                    disabled={isActionLoading}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Отменить запланированное
                  </Button>
                </>
              )}

              {/* State 4: Regular client */}
              {!isUrgent && !hasPlannedToday && !hasPendingVisit && (
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300"
                    variant="outline"
                    onClick={() => onSetUrgent(visitTargetId)}
                    disabled={isActionLoading}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Срочный
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" disabled={isActionLoading}>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        className="border border-orange-300 rounded-md mb-1 text-orange-800 dark:text-orange-300 focus:bg-orange-50 dark:focus:bg-orange-950"
                        onClick={() => onPlanToday(visitTargetId)}
                      >
                        <CalendarCheck className="h-4 w-4 mr-2" />
                        Заехать сегодня
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="border border-green-300 rounded-md mb-1 text-green-800 dark:text-green-300 focus:bg-green-50 dark:focus:bg-green-950"
                        onClick={() => onScheduleVisit(visitTargetId)}
                      >
                        <CalendarPlus className="h-4 w-4 mr-2" />
                        Запланировать визит
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="border border-blue-300 rounded-md text-blue-800 dark:text-blue-300 focus:bg-blue-50 dark:focus:bg-blue-950"
                        onClick={() => setScheduleDialogOpen(true)}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Запланировать дату
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </>
          )}
        </div>

        {/* Weekly Visits Section */}
        <div className="border-t pt-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setWeeklyVisitsOpen(true)}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Визиты на этой неделе ({weeklyVisits?.length ?? 0})
          </Button>
        </div>

        {/* Indoor Tasks Section */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Задачи у клиента
            </h4>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTaskInput(!showTaskInput)}
                title="Быстрое добавление"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCreateTaskOpen(true)}
                title="Расширенное создание"
              >
                <Expand className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {showTaskInput && (
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Новая задача..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTask()}
                disabled={createTask.isPending}
                autoFocus
              />
              <Button
                size="sm"
                onClick={handleCreateTask}
                disabled={!newTaskTitle.trim() || createTask.isPending}
              >
                {createTask.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          {tasksLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
            </div>
          ) : pendingTasks.length > 0 ? (
            <div className="space-y-1">
              {pendingTasks.map((task) => {
                const style = getTaskStyle({
                  type: task.type,
                  deadline: task.deadline,
                  isCompleted: false,
                  isCancelled: false,
                });

                return (
                  <div
                    key={task.id}
                    className={cn(
                      'flex items-center justify-between p-2 rounded text-sm cursor-pointer active:scale-[0.99] transition-all',
                      style.bg,
                      style.border,
                      'hover:opacity-80'
                    )}
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="truncate block">{task.title}</span>
                      {task.employee && (
                        <span className="text-xs text-muted-foreground">
                          {task.employee.lastName}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTaskToConfirm({ task, action: 'complete' });
                        }}
                        disabled={completeTask.isPending}
                        title="Выполнить"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTaskToConfirm({ task, action: 'cancel' });
                        }}
                        disabled={cancelTask.isPending}
                        title="Отменить задачу"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Нет задач</p>
          )}
        </div>

        {/* Cable Usage, Remont & Subcontractor Works Section */}
        <div className="border-t pt-4 space-y-2">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setCableUsageOpen(true)}
          >
            <Cable className="h-4 w-4 mr-2" />
            Расход кабеля
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setRemontOpen(true)}
          >
            <Wrench className="h-4 w-4 mr-2" />
            Ремонт в СЦ
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={openSubcontractorWorks}
          >
            <Hammer className="h-4 w-4 mr-2" />
            Работы субподрядчиков
          </Button>
          <ClientWarehouseSummary
            items={clientWarehouseItems}
            isLoading={warehouseLoading}
            isError={Boolean(warehouseError)}
            onItemClick={setSelectedWarehouseItem}
          />
        </div>
      </div>

      {/* Weekly Visits Sheet */}
      <WeeklyVisitsSheet
        clientId={clientId}
        open={weeklyVisitsOpen}
        onOpenChange={setWeeklyVisitsOpen}
      />

      {/* Task action confirmation dialog */}
      <AlertDialog open={!!taskToConfirm} onOpenChange={(open) => !open && setTaskToConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {taskToConfirm?.action === 'complete' ? 'Выполнить задачу?' : 'Отменить задачу?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Задача "{taskToConfirm?.task.title}" будет{' '}
              {taskToConfirm?.action === 'complete' ? 'отмечена как выполненная' : 'отменена'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Назад</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmTaskAction}
              className={
                taskToConfirm?.action === 'cancel'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {taskToConfirm?.action === 'complete' ? (
                completeTask.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )
              ) : cancelTask.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              {taskToConfirm?.action === 'complete' ? 'Выполнить' : 'Отменить задачу'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Was Before dialog */}
      <AlertDialog open={wasBeforeOpen} onOpenChange={setWasBeforeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Был раньше</AlertDialogTitle>
            <AlertDialogDescription>
              Укажите дату и время визита
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="visitDate">Дата</Label>
              <Input
                id="visitDate"
                type="date"
                value={wasBeforeDate}
                onChange={(e) => setWasBeforeDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visitTime">Время</Label>
              <Input
                id="visitTime"
                type="time"
                value={wasBeforeTime}
                onChange={(e) => setWasBeforeTime(e.target.value)}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWasBeforeConfirm}
              disabled={markVisited.isPending}
            >
              {markVisited.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Подтвердить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule Visit Dialog */}
      <ScheduleVisitDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        visitTargetId={visitTargetId}
        clientId={clientId}
        clientName={detail?.client.name}
      />

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(isOpen) => !isOpen && setSelectedTask(null)}
        onUpdate={handleUpdateTask}
        onComplete={handleCompleteTask}
        onCancel={handleCancelTask}
        isUpdating={updateTask.isPending}
        isCompleting={completeTask.isPending}
        isCancelling={cancelTask.isPending}
      />

      {/* Create Task Sheet */}
      <CreateTaskSheet
        open={createTaskOpen}
        onOpenChange={setCreateTaskOpen}
        onCreate={handleCreateTaskExpanded}
        isCreating={createTask.isPending}
        clientId={clientId}
        clientName={detail?.client.name}
      />

      {/* Cable Usage Dialog */}
      <CableUsageDialog
        open={cableUsageOpen}
        onOpenChange={setCableUsageOpen}
        clientName={detail?.client.name || ''}
      />

      {/* Client Remont Sheet */}
      <ClientRemontSheet
        clientId={clientId}
        clientName={detail?.client.name || ''}
        open={remontOpen}
        onOpenChange={setRemontOpen}
      />

      <WarehouseItemSheet
        item={selectedWarehouseItem}
        open={Boolean(selectedWarehouseItem)}
        onOpenChange={(open) => {
          if (!open) setSelectedWarehouseItem(null);
        }}
        isMine={selectedWarehouseItem?.isMine ?? false}
      />
    </>
  );
}
